import type { NostrEvent } from '@nostrify/nostrify';
import { parseImetaMap } from '@/lib/imeta';
import { IMAGE_EXTS, VIDEO_EXTS } from '@/lib/mediaUrls';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

/** Global matcher for image URLs in free text. */
const IMAGE_URL_GLOBAL = new RegExp(
  `https?:\\/\\/[^\\s]+\\.(${IMAGE_EXTS})(\\?[^\\s]*)?`,
  'gi',
);

/** Global matcher for video URLs in free text. */
const VIDEO_URL_GLOBAL = new RegExp(
  `https?:\\/\\/[^\\s]+\\.(${VIDEO_EXTS})(\\?[^\\s]*)?`,
  'gi',
);

export interface PostVideo {
  /** The video file URL. */
  url: string;
  /** Optional poster image (from imeta `image`/thumbnail) shown before playback. */
  poster?: string;
}

/**
 * Extracts a representative video from a post, checking (in order):
 * 1. NIP-94 `imeta` tags whose MIME type is a video (or whose URL looks like one)
 * 2. The first video URL found in the note's content
 *
 * Returns a sanitized https video URL (plus optional poster), or `undefined`.
 */
export function getPostVideo(event: NostrEvent): PostVideo | undefined {
  // 1. imeta tags
  const imeta = parseImetaMap(event.tags);
  for (const entry of imeta.values()) {
    const isVideo =
      entry.mime?.startsWith('video/') ||
      new RegExp(`\\.(${VIDEO_EXTS})(\\?.*)?$`, 'i').test(entry.url);
    if (isVideo) {
      const url = sanitizeUrl(entry.url);
      if (url) {
        const poster = entry.thumbnail
          ? sanitizeUrl(entry.thumbnail)
          : undefined;
        return { url, poster };
      }
    }
  }

  // 2. First video URL in content
  const matches = event.content.match(VIDEO_URL_GLOBAL);
  if (matches && matches.length > 0) {
    const url = sanitizeUrl(matches[0]);
    if (url) return { url };
  }

  return undefined;
}

/**
 * Extracts a representative image URL from a post, checking (in order):
 * 1. NIP-94 `imeta` tags whose MIME type is an image (or whose URL looks like one)
 * 2. The first image URL found in the note's content
 *
 * Returns a sanitized https URL, or `undefined` if the post has no image.
 */
export function getPostImage(event: NostrEvent): string | undefined {
  // 1. imeta tags
  const imeta = parseImetaMap(event.tags);
  for (const entry of imeta.values()) {
    const isImage =
      entry.mime?.startsWith('image/') ||
      new RegExp(`\\.(${IMAGE_EXTS})(\\?.*)?$`, 'i').test(entry.url);
    if (isImage) {
      const url = sanitizeUrl(entry.thumbnail || entry.url);
      if (url) return url;
    }
  }

  // 2. First image URL in content
  const matches = event.content.match(IMAGE_URL_GLOBAL);
  if (matches && matches.length > 0) {
    const url = sanitizeUrl(matches[0]);
    if (url) return url;
  }

  return undefined;
}

/**
 * Returns the note's content with media URLs stripped out, trimmed —
 * useful for text-only thumbnail previews.
 */
export function getPostText(event: NostrEvent): string {
  return event.content
    .replace(IMAGE_URL_GLOBAL, '')
    .replace(/https?:\/\/\S+/g, (u) => u) // keep other links as-is
    .trim();
}
